/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Suspense, useRef, useState } from 'react';
import Media from 'react-media';

import ImgixWithPrioritizedLoading from 'js/components/ImgixWithPrioritizedLoading';

import { Grid, Typography, Typography2, breakpoints } from '@coursera/cds-core';

import useUserAgent from 'bundles/page/hooks/useUserAgent';
import LearningProductCardImage from 'bundles/product-card/components/LearningProductCardImage';
import CoachButtonInHeroModule from 'bundles/unified-description-page-common/components/coach/CoachButtonInHeroModule';
import DefaultHeroImageSvg from 'bundles/unified-description-page-common/components/modules/hero/DefaultHeroImageSvg';
import HeroPreviewThumbnail from 'bundles/unified-description-page-common/components/modules/hero/HeroPreviewThumbnail';
import {
  NARROW_XDP_CONTAINER_QUERY,
  twoPanelGridItemFullWidth,
} from 'bundles/unified-description-page-common/constants/styles';
import { useHeroImageExperiment } from 'bundles/unified-description-page-common/contexts/HeroImageExperimentContext';
import { usePreviewVideoExperiment } from 'bundles/unified-description-page-common/contexts/PreviewVideoExperimentContext';
import { usePreviewVideoTracking } from 'bundles/unified-description-page-common/hooks/usePreviewVideoTracking';
import useUpdateCtaPosition from 'bundles/unified-description-page-common/hooks/useUpdateCtaPosition';
import { useCoachCareerFitAssistantData } from 'bundles/unified-description-page-common/providers/specialization/CoachCareerFitAssistantProvider';

const PreviewVideoModal = React.lazy(
  () => import('bundles/unified-description-page-common/components/modules/hero/PreviewVideoModal')
);

type Props = {
  productTitle: string;
  productTag?: React.ReactNode;
  partnerLogo?: React.ReactNode;
  description?: React.ReactNode;
  primaryCta?: React.ReactNode;
  previewModalCta?: React.ReactNode;
  secondaryCta?: React.ReactNode;
  additionalInfo?: React.ReactNode;
  /**
   * Currently this is only used for Google Hero Images
   */
  secondaryInfo?: React.ReactNode;
  instructors?: React.ReactNode;
  additionalCta?: React.ReactNode;
  backgroundImage?: string;
  backgroundImageElement?: React.ReactNode;
  hideBackgroundImage?: boolean;
  /** Reserve the primary CTA's height so the hero doesn't shift when the enroll button rendering during CSR. */
  reservePrimaryCtaSpace?: boolean;
  isCompact?: boolean;
};

// Forces enroll children to fill their parent — used wherever the hero's
// CTAs need to span the full row (sm-down viewport, and any time the
// chat-narrowed XDP column triggers).
const FULL_WIDTH_CTA_CHILDREN_CSS = `
  button,
  a,
  form {
    width: 100% !important;
    max-width: 100%;
  }
`;

// Stacks the CTA row vertically with full-width children.
const STACKED_CTAS_CSS = `
  flex-direction: column;
  align-items: normal;
  gap: var(--cds-spacing-200);
  ${FULL_WIDTH_CTA_CHILDREN_CSS}
`;

const styles = {
  container: css`
    padding: var(--cds-spacing-600) 0 88px;
    position: relative;

    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-400) 0 var(--cds-spacing-300);
    }
    ${NARROW_XDP_CONTAINER_QUERY} {
      padding: var(--cds-spacing-400) 0 var(--cds-spacing-300);
    }
  `,
  previewVideo: css`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: var(--cds-spacing-100);
    width: 450px;
    height: 235px;
    z-index: 1000;

    ${breakpoints.up('sm')} {
      max-width: 40%;
    }
  `,
  previewVideoMobile: css`
    margin-top: var(--cds-spacing-300);
    width: 100%;
  `,
  backgroundImage: css`
    position: absolute;
    right: 0;
    bottom: calc(var(--cds-spacing-600) * -1);

    ${NARROW_XDP_CONTAINER_QUERY} {
      display: none;
    }
  `,
  productTitle: css`
    margin-right: var(--cds-spacing-100);
  `,
  titleTag: css`
    display: inline-flex;
    vertical-align: middle;
  `,
  content: css`
    position: relative;
  `,
  logo: css`
    ${breakpoints.down('sm')} {
      margin-top: 0;
    }
  `,
  instructors: css`
    margin-top: var(--cds-spacing-50);
  `,
  description: css`
    margin-top: var(--cds-spacing-50);
  `,
  ctas: css`
    display: flex;
    align-items: baseline;

    ${breakpoints.down('sm')} {
      ${STACKED_CTAS_CSS}
    }
    ${NARROW_XDP_CONTAINER_QUERY} {
      ${STACKED_CTAS_CSS}
    }
  `,
  // This has the unfortunate job of adjusting styling for consumer and enterprise enroll buttons.
  // We import these buttons from other bundles so don't have direct control over styling.
  primaryCta: css`
    margin-top: var(--cds-spacing-400);

    button,
    a,
    form {
      width: auto !important;
    }

    form button {
      padding-left: var(--cds-spacing-1000);
      padding-right: var(--cds-spacing-1000);
    }

    ${breakpoints.down('sm')} {
      ${FULL_WIDTH_CTA_CHILDREN_CSS}
    }
    ${NARROW_XDP_CONTAINER_QUERY} {
      ${FULL_WIDTH_CTA_CHILDREN_CSS}
    }
  `,
  reservedPrimaryCtaSpaceCss: css`
    min-height: 70px;
    min-width: 160px;
  `,
  additionalCta: css`
    margin-top: var(--cds-spacing-100);
  `,
  additionalInfo: css`
    margin-top: var(--cds-spacing-150);
  `,
  coachButtonContainer: css`
    position: absolute;
    bottom: 0;
    right: var(--cds-spacing-100);

    ${breakpoints.down('sm')} {
      position: relative;
      margin-bottom: calc(var(--cds-spacing-100) * -1);
      right: unset;
      bottom: unset;
    }
  `,
  previewAndCoachColumn: css`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: var(--cds-spacing-200);
  `,
  previewVideoInFlow: css`
    width: 100%;
    max-width: 450px;
  `,
  coachButtonContainerInFlow: css`
    position: static;
  `,
};

const isCompactStyles = {
  container: css`
    padding: var(--cds-spacing-200);
    position: relative;
  `,
};

// The hero image treatment's source images are trusted to already be square, so unlike the preview
// video thumbnail, this isn't forced into a fixed landscape box — it's sized to its own aspect
// ratio so nothing gets cropped.
//
// The narrow-width state: in normal flow at the bottom of the hero, under the enroll CTA and the
// additional meta, capped so a square image doesn't dominate the viewport. Also a complete reset of
// the offset treatment below, so this block can be re-applied wholesale by the container query
// after the wider viewport rules have already matched.
const heroImageNarrowCss = css`
  position: static;
  transform: none;
  z-index: auto;
  margin-top: var(--cds-spacing-300);
  width: 100%;
  max-width: 342px;

  img {
    display: block;
    width: 100%;
    height: auto;
    border-radius: var(--cds-border-radius-200);
    aspect-ratio: 16 / 9;
    object-fit: cover;
  }
`;

// Two independent signals narrow the hero, so both gate the treatment:
//
//   - viewport width, for phones and small windows;
//   - the width of the `page-content` container, for the two-panel layout, where docking the chat
//     panel narrows the content column while the viewport stays wide.
//
// The container query can't replace the media query: `page-content` is only established inside the
// two-panel and embedded shells, so on a standard XDP it never matches and the media query is the
// only thing that fires. It also can't be nested inside the media query for the same reason. So the
// narrow state is the unqueried base, the offset treatment applies at md and up, and the container
// query re-applies the narrow state last, when the docked column is too narrow for the offset one.
//
// Keeping the switch in CSS rather than a JS viewport gate means the correct treatment is painted
// on the first frame, so the image can't flash in and then disappear on hydration.
const heroImageContainerCss = css`
  ${heroImageNarrowCss};

  ${breakpoints.up('md')} {
    position: absolute;
    top: 110px;
    right: var(--cds-spacing-100);
    z-index: 1000;
    margin-top: 0;
    width: 342px;
    max-width: 40%;
  }

  ${breakpoints.down('sm')} {
    max-width: 600px;
    margin-top: var(--cds-spacing-400);
  }

  ${NARROW_XDP_CONTAINER_QUERY} {
    ${heroImageNarrowCss};
    max-width: 600px;
    margin-top: var(--cds-spacing-400);
  }
`;

const HeroModule = ({
  productTitle,
  productTag,
  partnerLogo,
  description,
  primaryCta,
  previewModalCta,
  secondaryCta,
  additionalInfo,
  secondaryInfo,
  instructors,
  additionalCta,
  backgroundImage,
  backgroundImageElement,
  hideBackgroundImage,
  reservePrimaryCtaSpace,
  isCompact,
}: Props) => {
  const userAgent = useUserAgent();
  const pageCtaRef = useRef<HTMLDivElement | null>(null);
  useUpdateCtaPosition(pageCtaRef);

  const experimentData = usePreviewVideoExperiment();
  const { experimentVariant, rating, ratingCount, enrollmentCount, previewVideos, notifyPreviewVideoInterest } =
    experimentData;
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const { trackOpenPreviewVideo } = usePreviewVideoTracking('hero');

  const openPreviewModal = () => {
    if (previewVideos[0]) {
      trackOpenPreviewVideo(previewVideos[0]);
    }
    // Guaranteed fallback for touch/keyboard opens that skipped the thumbnail's hover/focus prefetch.
    notifyPreviewVideoInterest();
    setIsPreviewModalOpen(true);
  };

  const { heroImageUrl } = useHeroImageExperiment();
  const { coachCareerFitAssistantEnabled } = useCoachCareerFitAssistantData();
  const shouldShowPreviewVideo =
    ['trailer-videos-only', 'trailer-and-demo-videos'].includes(experimentVariant) && previewVideos.length > 0;

  const imgParams = {
    quality: 80,
  };

  const getTitle = () => {
    if (isCompact) {
      return (
        <Typography2 data-e2e="hero-title" variant="subtitleLarge" component="h1">
          <span css={styles.productTitle}>{productTitle}</span>
          {productTag && <span css={styles.titleTag}>{productTag}</span>}
        </Typography2>
      );
    }

    return (
      <>
        {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
        <Typography data-e2e="hero-title" variant="d2semibold" component="h1">
          <span css={styles.productTitle}>{productTitle}</span>
          {productTag && <span css={styles.titleTag}>{productTag}</span>}
        </Typography>
      </>
    );
  };

  const getAdditionalInfo = () => {
    return (
      <>
        {additionalInfo && <div css={styles.additionalInfo}>{additionalInfo}</div>}
        {additionalCta && <div css={styles.additionalCta}>{additionalCta}</div>}
      </>
    );
  };

  const renderBackgroundImage = () => {
    if (isCompact) {
      return null;
    }

    // The hero image treatment replaces the default background decoration entirely. It renders
    // outside this viewport-gated path so CSS alone owns its desktop/mobile switch.
    if (heroImageUrl) {
      return null;
    }

    if (backgroundImage) {
      return (
        <ImgixWithPrioritizedLoading
          src={backgroundImage}
          alt=""
          width={566}
          imgParams={imgParams}
          css={styles.backgroundImage}
        />
      );
    }

    if (!secondaryInfo) {
      return <div css={styles.backgroundImage}>{backgroundImageElement || <DefaultHeroImageSvg width={566} />}</div>;
    }

    return null;
  };

  // Rendered once, unconditionally, and positioned by CSS. A single node keeps it to one image
  // request and leaves nothing for hydration to add or remove.
  const renderHeroExperimentImage = () => {
    if (isCompact || !heroImageUrl) {
      return null;
    }

    /* We should be sure to use productCardV2Utils if we roll this out */
    return (
      <div css={heroImageContainerCss}>
        <LearningProductCardImage
          imageParams={{
            width: 342,
            height: 192,
          }}
          imageUrl={heroImageUrl}
        />
      </div>
    );
  };

  return (
    <Media query={{ maxWidth: breakpoints.values.md - 1 }} defaultMatches={userAgent?.isMobileBrowser}>
      {(matches) => {
        const shouldStackPreviewAndCoach =
          !matches && !hideBackgroundImage && !isCompact && shouldShowPreviewVideo && coachCareerFitAssistantEnabled;

        return (
          <div css={[styles.container, isCompact && isCompactStyles.container]}>
            {!matches && !hideBackgroundImage && renderBackgroundImage()}
            {!matches &&
              !isCompact &&
              !hideBackgroundImage &&
              shouldShowPreviewVideo &&
              !coachCareerFitAssistantEnabled && (
                <div css={styles.previewVideo}>
                  <HeroPreviewThumbnail
                    data={previewVideos[0]!}
                    onClick={openPreviewModal}
                    onPreviewIntent={notifyPreviewVideoInterest}
                  />
                </div>
              )}
            <Grid container data-e2e="hero-module" spacing={16} css={styles.content}>
              <Grid item xs={12}>
                {partnerLogo && <div css={styles.logo}>{partnerLogo}</div>}
              </Grid>

              <React.Fragment>
                <Grid item xs={12} md={matches ? 12 : 7} css={twoPanelGridItemFullWidth}>
                  {getTitle()}
                  {description && <div css={styles.description}>{description}</div>}
                  {instructors && <div css={styles.instructors}>{instructors}</div>}
                  {primaryCta && (
                    <div css={styles.ctas}>
                      <div
                        id="hero-primary-cta"
                        css={[styles.primaryCta, reservePrimaryCtaSpace && styles.reservedPrimaryCtaSpaceCss]}
                        ref={pageCtaRef}
                      >
                        {primaryCta}
                      </div>
                      {secondaryCta}
                    </div>
                  )}
                  {getAdditionalInfo()}
                </Grid>
                {!matches && secondaryInfo && (
                  <Grid item md={5}>
                    {secondaryInfo}
                  </Grid>
                )}
                {coachCareerFitAssistantEnabled && (
                  <Grid item xs={12} md={matches ? 12 : 5}>
                    {shouldStackPreviewAndCoach ? (
                      <div css={styles.previewAndCoachColumn}>
                        <div css={styles.previewVideoInFlow}>
                          <HeroPreviewThumbnail
                            data={previewVideos[0]!}
                            onClick={openPreviewModal}
                            onPreviewIntent={notifyPreviewVideoInterest}
                          />
                        </div>
                        <div css={styles.coachButtonContainerInFlow}>
                          <CoachButtonInHeroModule />
                        </div>
                      </div>
                    ) : (
                      <div css={styles.coachButtonContainer}>
                        <CoachButtonInHeroModule />
                      </div>
                    )}
                  </Grid>
                )}
              </React.Fragment>
            </Grid>
            {matches && shouldShowPreviewVideo && (
              <div css={styles.previewVideoMobile}>
                <HeroPreviewThumbnail
                  data={previewVideos[0]!}
                  onClick={openPreviewModal}
                  onPreviewIntent={notifyPreviewVideoInterest}
                />
              </div>
            )}
            {!hideBackgroundImage && renderHeroExperimentImage()}
            {shouldShowPreviewVideo && (
              <Suspense fallback={null}>
                <PreviewVideoModal
                  open={isPreviewModalOpen}
                  onClose={() => setIsPreviewModalOpen(false)}
                  videos={previewVideos}
                  name={productTitle}
                  placement="hero"
                  partnerLogoUrl={experimentData.partnerLogoUrl}
                  partnerName={experimentData.partnerName}
                  rating={rating}
                  ratingCount={ratingCount}
                  enrollmentCount={enrollmentCount}
                  enrollButton={previewModalCta ?? primaryCta}
                />
              </Suspense>
            )}
          </div>
        );
      }}
    </Media>
  );
};

export default HeroModule;
